//Lua decompiler here
enum LUA_OBJTYPES {
	NULL = 0,
	BOOLEAN = 1,
	FLOAT = 3,
	SHORT_STRING = 4,
	INTEGER = 19,
	LONG_STRING = 20
}

enum LUA_OPCODES {
	MOVE = 0,
	LOADK = 1,
	GETTABUP = 6,
	SET_TABLE = 10,
	DECLARE_TABLE = 11,
	RETURN = 38,
	SET_LIST = 43,
	CLOSURE = 44
}

export class PNCxLuaReader {

	file:Uint8Array<ArrayBufferLike>;
	dataview:DataView;
	cursor:number = 0;
	private readonly textDecoder = new TextDecoder('utf-8');

	constructor(file:Uint8Array<ArrayBufferLike>) {
		this.file = file;
		this.dataview = new DataView(this.file.buffer, this.file.byteOffset, this.file.byteLength);
	}

	private readUChar() {
		//const ret = this.file[this.cursor];
		const ret = this.dataview.getUint8(this.cursor);
		this.cursor++;
		return ret;
	}

	private readUInt32() {
		const ret = this.dataview.getUint32(this.cursor, true);
		this.cursor+=4;
		return ret;
	}

	private readLong() {
		const ret = Number(this.dataview.getBigInt64(this.cursor, true));
		this.cursor+=8;
		return ret;
	}

	private readDouble() {
		const ret = this.dataview.getFloat64(this.cursor, true);
		this.cursor += 8;
		return ret;
	}

	private readBool() {
		return this.readUChar() === 0x1;
	}

	private readLuaString() {
		let size = this.readUChar();
		if (size >= 255) {
			size = this.readUInt32();
		}
		const subArray = this.file.subarray(this.cursor, this.cursor+size-1);
		this.cursor += size-1;
		const ret = this.textDecoder.decode(subArray);
		return ret;
	}

	private readHeader(warnIfNonStandard:boolean = true) {
		const signature = this.file.subarray(this.cursor, this.cursor + 4);
		this.cursor += 4;
		if (
			signature[0] !== 0x1B ||
			signature[1] !== 0x4C ||
			signature[2] !== 0x75 ||
			signature[3] !== 0x61
		) {
			throw new TypeError(`Invalid Lua bytecode file. Got ${Array.from(signature).map((b)=>b.toString(16).padStart(2, '0')).join(' ')}`);
		}

		const versionByte = this.readUChar();
		const verMajor = (versionByte & 0xF0) >> 4;
		const verMinor = versionByte & 0x0F;
		const version = verMajor + verMinor / 10;

		const isNonStandard = this.readBool();
		if (warnIfNonStandard && isNonStandard) {
			console.warn('Input luac file is non-standard, assuming this is tencent xLua file.');
		}

		// Skip luac data / header bytes before size fields.
		this.cursor += 6;

		const sizeOfInt = this.readUChar();
		let sizeOfSizeT:number | undefined = undefined;
		if (!isNonStandard) {
			sizeOfSizeT = this.readUChar();
		}
		const sizeOfInstruction = this.readUChar();
		const sizeOfLuaInteger = this.readUChar();
		const sizeOfLuaNumber = this.readUChar();

		const luacInt = this.readDouble();
		const luacNum = this.readDouble();

		return {
			version,
			isNonStandard,
			sizeOfInt,
			sizeOfSizeT,
			sizeOfInstruction,
			sizeOfLuaInteger,
			sizeOfLuaNumber,
			luacInt,
			luacNum
		};
	}

	private readDebugSymbols() {
		this.readUChar(); // sizeup_values
		const sourceFile = this.readLuaString();
		console.assert(!!sourceFile, 'Misaligned reader!');
		this.readUInt32(); // line_defined
		this.readUInt32(); // last_line_defined
	}

	searchPattern(uint8Array:Uint8Array<ArrayBufferLike>, pattern:Array<number>) {
		const arrayLength = uint8Array.length;
		const patternLength = pattern.length;
	
		for (let i = 0; i <= arrayLength - patternLength; i++) {
			let found = true;
			for (let j = 0; j < patternLength; j++) {
				if (uint8Array[i + j] !== pattern[j]) {
					found = false;
					break;
				}
			}
			if (found) {
				return i; // Return the starting index of the pattern
			}
		}
		return -1; // Return -1 if the pattern is not found
	}

	#interpretAssembly(consts:Array<number | string | boolean | null> = [], warnIfNonStandard:boolean = true) {
		//This code was machine translated from Python!!!
		//Please read the source code (xLuacParser.py) if you want to know how it works.
		let reconstructedTable:Record<number | string, any> = {};
		this.readHeader(warnIfNonStandard);
		this.readDebugSymbols();

		this.readUChar(); // num_params
		this.readBool(); // is_vararg
		const maxStackSize = this.readUChar();
		const codeSectionSize = this.readUInt32();
		const end = this.cursor + codeSectionSize * 4;

		const REGISTERS:Array<any> = new Array(maxStackSize).fill(null);

		while (this.cursor < end) {
			const opcodeAndValues = this.readUInt32();
			const opcode = opcodeAndValues & 0x3F;
			const values = opcodeAndValues >>> 6;

			const A = values & 0xFF;
			const B = (values >>> 8) & 0x1FF;
			const C = (values >>> 17) & 0x1FF;

			switch (opcode) {
				case LUA_OPCODES.MOVE: {
					REGISTERS[A] = REGISTERS[B];
					break;
				}
				case LUA_OPCODES.LOADK: {
					const register = A;
					const key = values >>> 8;
					if (register < REGISTERS.length) {
						REGISTERS[register] = key < consts.length ? consts[key] : null;
					}
					break;
				}
				case LUA_OPCODES.GETTABUP: {
					const register = A;
					if (register < REGISTERS.length) {
						REGISTERS[register] = null;
					}
					break;
				}
				case LUA_OPCODES.SET_TABLE: {
					const tableIndexPtr = C;
					const tableValuePtr = B;
					const tableIndex = tableIndexPtr >= 256 ? consts[tableIndexPtr - 256] : REGISTERS[tableIndexPtr];
					const tableValue = tableValuePtr >= 256 ? consts[tableValuePtr - 256] : REGISTERS[tableValuePtr];

					if (typeof REGISTERS[A] !== 'object' || REGISTERS[A] === null || Array.isArray(REGISTERS[A])) {
						REGISTERS[A] = {};
					}
					if (tableIndex !== null && tableIndex !== undefined) {
						(REGISTERS[A] as Record<number | string, number | string | boolean | null>)[tableIndex as number | string] = (tableValue ?? null) as number | string | boolean | null;
					}
					break;
				}
				case LUA_OPCODES.DECLARE_TABLE: {
					const register = A;
					if (register < REGISTERS.length) {
						REGISTERS[register] = {};
					}
					break;
				}
				case LUA_OPCODES.SET_LIST: {
					const tableReg = A;
					const numValues = C;
					const blockIndex = B;

					if (tableReg < REGISTERS.length) {
						if (typeof REGISTERS[tableReg] !== 'object' || REGISTERS[tableReg] === null || Array.isArray(REGISTERS[tableReg])) {
							REGISTERS[tableReg] = {};
						}
						const table = REGISTERS[tableReg] as Record<number | string, number | string | boolean | null>;

						const blockSize = 50;
						const startIdx = blockIndex > 0 ? (blockIndex - 1) * blockSize + 1 : 1;

						for (let idx = startIdx; idx < startIdx + numValues; idx++) {
							const srcReg = tableReg + (idx - startIdx) + 1;
							if (srcReg < REGISTERS.length && REGISTERS[srcReg] !== null && REGISTERS[srcReg] !== undefined) {
								table[idx] = REGISTERS[srcReg] as number | string | boolean;
							}
						}
					}
					break;
				}
				case LUA_OPCODES.RETURN: {
					if (C >= 2) {
						if (typeof REGISTERS[A] === 'object' && REGISTERS[A] !== null && !Array.isArray(REGISTERS[A])) {
							reconstructedTable = REGISTERS[A] as Record<number | string, number | string | boolean | null>;
						}
					} else {
						for (let i = REGISTERS.length - 1; i >= 0; i--) {
							const reg = REGISTERS[i];
							if (typeof reg === 'object' && reg !== null && !Array.isArray(reg)) {
								reconstructedTable = reg as Record<number | string, number | string | boolean | null>;
								break;
							}
						}
					}
					this.cursor = 0;
					return reconstructedTable;
				}
				default: {
					console.warn(`ignoring unknown opcode ${opcode}`);
					break;
				}
			}
		}

		this.cursor = 0;
		return reconstructedTable;
	}

	#parseConstTable() {
		const result = this.searchPattern(this.file, [0x26, 0x00, 0x80, 0x00])
		console.log("Found match at 0x"+result.toString(16));

		if (result > 0) {
			this.cursor = result + 4;
			const numLuaConsts = this.readUInt32();
			const consts:Array<number | string | boolean | null> = new Array(numLuaConsts).fill(null);

			console.log(`${numLuaConsts} structs to parse`);
			console.log("0x"+this.cursor.toString(16));

			for (let i = 0; i < numLuaConsts; i++) {
				const objCode = this.readUChar();

				if (objCode == LUA_OBJTYPES.INTEGER) {
					consts[i] = this.readLong();
				}
				else if (objCode == LUA_OBJTYPES.FLOAT) {
					consts[i] = this.readDouble();
				}
				else if (objCode == LUA_OBJTYPES.BOOLEAN) {
					consts[i] = this.readBool();
				}
				else if (objCode == LUA_OBJTYPES.SHORT_STRING || objCode == LUA_OBJTYPES.LONG_STRING) {
					const st = this.readLuaString();
					consts[i] = st;
				}
				else {
					console.error("Unknown obj code encountered!!! ",objCode);
				}
			}

			this.cursor = 0;
			return consts;
		}
		return [];
	}

	readFile() {
		console.assert(this.file != undefined);
		const consts = this.#parseConstTable();
		if (consts.length === 0) {
			return {};
		}
		return this.#interpretAssembly(consts, true);
	}
}

export namespace XLuaUtilities {
	function insertByte(
		original: Uint8Array,
		index: number,
		value: number
		): Uint8Array {
		const result = new Uint8Array(original.length + 1);

		// Copy before insertion point
		result.set(original.subarray(0, index), 0);

		// Insert new value
		result[index] = value;

		// Copy after insertion point
		result.set(original.subarray(index), index + 1);

		return result;
	}

	export function checkLuacHeader(contents:Uint8Array) {

		const header = [0x1B, 0x4C, 0x75, 0x61, 0x53] // '0x1B L u a S'
		for (let i = 0; i < 5; i++) {
			if (contents[i] !== header[i]) return false;
		}
		return true;
	}


 /**
  * If necessary, patches xlua to a normal lua bytecode. If normal bytecode is given, it will simply return the same file.
  * @param {Uint8Array<ArrayBufferLike>} b The bytecode to patch.
  * @returns {Uint8Array<ArrayBufferLike>} Returns the patched file.
  */
	export function patchIfNecessary(b:Uint8Array<ArrayBufferLike>) {
		
		if (b[0x05] == 1) {
			/*if (!PatchXLuaBytecode.check_luac_header(b)) {
				throw new TypeError("The given file was not lua bytecode");
			}*/
			b = insertByte(b, 0x0D, 0x04)
			b[0x05] = 0;
		}
		return b;
	}
}